import type { TFunction } from "i18next"
import { DateTime } from "luxon"
import fp from "lodash/fp"

import type {
  DeviceStatus,
  IDeviceWRelsById,
  DeviceReportingStatus,
  Device,
  DeviceStatusesById,
  IDevsWRelsByIdWStates,
  DevsWRelsByIdWStatesNStatus,
  DeviceRowInfo,
  InstalledOnInfo,
  DeviceSettings,
  DeviceWGroupsNStates,
} from "types/device.types"
import type { RecordType } from "types/dashboard.types"
import type { OrgAlertSettingsStatesRes } from "types/orgs.types"
import type {
  AlertSettingWState,
  DeviceAlertSetting,
  DeviceAlertSettingWState,
} from "types/alerts.types"
import type { Tower } from "types/towers.types"
import type { Powerline } from "types/powerlines.types"

export const getBatteryStatus = (
  deviceStatus: DeviceStatus | undefined,
  statusesLoading: boolean,
): {
  value: number | null
  status: string | null
  timestamp: string | null
  isLoading: boolean
} => {
  return {
    value: deviceStatus?.battery_level ?? null,
    status: deviceStatus?.battery_status ?? null,
    timestamp: deviceStatus?.battery_data_received_at ?? null,
    isLoading: statusesLoading,
  }
}

export const getLastMessageDateTime = (
  status: DeviceStatus | undefined,
): DateTime | undefined => {
  if (status?.last_message) {
    return DateTime.fromISO(status.last_message)
  }
  return undefined
}

export const getDeviceReportingStatus = (
  status: DeviceStatus | undefined,
  now: DateTime,
): DeviceReportingStatus => {
  const lastMessageDateTime = getLastMessageDateTime(status)
  const missedExpectedReport =
    status?.next_expected_report &&
    now > DateTime.fromISO(status.next_expected_report).plus({ minutes: 2 })

  if (!lastMessageDateTime) {
    return "unknown"
  }

  if (lastMessageDateTime && now.minus({ minutes: 5 }) < lastMessageDateTime) {
    return "reporting"
  } else if (lastMessageDateTime && now.minus({ days: 2 }) < lastMessageDateTime) {
    return missedExpectedReport ? "intermittent" : "active"
  } else {
    return "inactive"
  }
}

const SEVERITY_BY_STATUS: Record<DeviceReportingStatus, number> = {
  inactive: 1,
  unknown: 2,
  intermittent: 3,
  active: 4,
  reporting: 5,
}

// Puts devices with an alert first, then the rest from the worst reporting status to the best.
// Devices with the same severity keep their order.
export const sortDevicesBySeverity = <
  T extends { status: DeviceReportingStatus; hasAlert: boolean },
>(
  devices: T[],
): T[] => {
  const severity = (device: T): number =>
    device.hasAlert
      ? 0
      : (SEVERITY_BY_STATUS[device.status] ?? SEVERITY_BY_STATUS.inactive)
  return [...devices].sort((a, b) => severity(a) - severity(b))
}

export const getDevicesByStatus = (
  devicesWithStatusById: DevsWRelsByIdWStatesNStatus,
): RecordType[] => {
  const now = DateTime.now()
  const names = {
    reporting: "reporting_status.REPORTING",
    active: "reporting_status.ACTIVE",
    intermittent: "reporting_status.INTERMITTENT",
    inactive: "reporting_status.INACTIVE",
    unknown: "reporting_status.UNKNOWN",
  }
  const orders = {
    reporting: 1,
    active: 2,
    intermittent: 3,
    inactive: 4,
    unknown: 5,
  }

  return fp.flow(
    fp.defaultTo({}),
    fp.values,
    fp.countBy(({ status }: { status: DeviceStatus }) =>
      getDeviceReportingStatus(status, now),
    ),
    fp.defaults({
      reporting: 0,
      active: 0,
      intermittent: 0,
      inactive: 0,
      unknown: 0,
    }),
    fp.toPairs,
    fp.map(([key, value]: [DeviceReportingStatus, number]) => ({
      colorKey: key,
      nameKey: names[key],
      value,
      order: orders[key],
    })),
  )(devicesWithStatusById)
}

export const getDevicesByAlertStatus = (
  devsWRelsById: IDeviceWRelsById | undefined,
  orgDevicesAlertSettingsWStates: DeviceAlertSettingWState[],
  orgActiveDevicesAlertSettingsById: { [key: string]: DeviceAlertSetting },
): RecordType[] => {
  const isActive = (alert: AlertSettingWState) =>
    !!orgActiveDevicesAlertSettingsById[alert.id]
  const isTriggered = (alert: AlertSettingWState) =>
    !!alert.state?.triggered && !alert.state?.silenced
  const isInstalledDeviceAlert = (alert: AlertSettingWState) =>
    !!devsWRelsById?.[alert.entity_id]

  const triggeredDevicesAmount = fp.flow(
    fp.defaultTo([] as OrgAlertSettingsStatesRes),
    fp.filter(fp.allPass([isActive, isTriggered, isInstalledDeviceAlert])),
    fp.map("entity_id"),
    fp.uniq,
    fp.size,
  )(orgDevicesAlertSettingsWStates)
  const notTriggeredDevicesAmount = fp.size(devsWRelsById) - triggeredDevicesAmount
  return [
    {
      colorKey: "triggered",
      nameKey: "alerts.WITH_TRIGGERED",
      value: triggeredDevicesAmount,
      order: 1,
    },
    {
      colorKey: "notTriggered",
      nameKey: "alerts.WITHOUT_TRIGGERED",
      value: notTriggeredDevicesAmount,
      order: 2,
    },
  ]
}

// Receives a version and a target and checks if the version is above or equal to the target version
export const isVersionAboveOrEqual = (version: string, target: string): boolean => {
  if (!version) return false
  const [vMajor, vMinor] = version.split(".").map(Number)
  const [tMajor, tMinor] = target.split(".").map(Number)

  return vMajor > tMajor || (vMajor === tMajor && vMinor >= tMinor)
}

// Receives a device and returns its version and the target for comparison depending on device_version and hardware_version
// Note: At some point, the version should only depend on device_version
export const getDeviceVersionAndTarget = (
  device: Device,
): { version: string; target: string } => {
  const deviceVersion = device.settings?.device_version ?? ""
  const hwVersion = device.hardware_version ?? ""
  const [, deviceVersionPart] = deviceVersion.split("v")
  const [, hwVersionPart] = hwVersion.split("-")
  const version = deviceVersionPart ?? hwVersionPart
  const target = deviceVersionPart ? "3.5" : "3.3"

  return { version, target }
}

export const isVirtualDevice = (deviceType: string): boolean => {
  return (
    deviceType === "virtual_span" ||
    deviceType === "virtual_powerline" ||
    deviceType === "virtual_tower"
  )
}

export const isVirtualSpan = (deviceType: string): boolean => {
  return deviceType === "virtual_span"
}

export const isVirtualPowerline = (deviceType: string): boolean => {
  return deviceType === "virtual_powerline"
}

export const isVirtualTower = (deviceType: string): boolean => {
  return deviceType === "virtual_tower"
}

export const formatDevicesWithStatus = (
  devices: IDevsWRelsByIdWStates,
  statuses: DeviceStatusesById,
): DevsWRelsByIdWStatesNStatus =>
  fp.mapValues(
    (value) => ({ ...value, status: statuses[value.device.less_id] || {} }),
    devices,
  )

/** A device only counts as installed once it names the span or tower it is on. */
export const isDeviceInstalled = (settings: DeviceSettings | undefined): boolean => {
  switch (settings?.installation_type) {
    case "powerline":
      return !!settings.span_id
    case "tower":
      return !!settings.tower_id
    default:
      return false
  }
}

export const getInstalledOnInfo = (
  settings: DeviceSettings | undefined,
  powerline: Powerline | undefined,
  tower: Tower | undefined,
): InstalledOnInfo => {
  if (!isDeviceInstalled(settings)) return {}

  switch (settings?.installation_type) {
    case "powerline":
      return { type: "span", spanId: settings.span_id, powerline }
    case "tower":
      return { type: "tower", tower, powerline }
    default:
      return {}
  }
}

export const formatDevicesWithInstalledOnById = (
  devices: DevsWRelsByIdWStatesNStatus,
  towersById: Record<number, Tower>,
  powerlinesById: Record<number, Powerline>,
): Record<string, DeviceRowInfo> =>
  fp.mapValues((row) => {
    const settings = row.device?.settings
    const installationType = settings?.installation_type
    const tower =
      installationType === "tower" && settings?.tower_id
        ? towersById[settings.tower_id]
        : undefined
    // A tower device carries its line too, so both installation types can name
    // the line they belong to.
    const powerline = settings?.powerline_id
      ? powerlinesById[settings.powerline_id]
      : undefined

    return {
      ...row,
      installedOn: getInstalledOnInfo(settings, powerline, tower),
    }
  }, devices)

export const getDevicesByInstallationStatus = (
  devsWRelsByIdWStates: IDevsWRelsByIdWStates,
): {
  notInstalled: IDevsWRelsByIdWStates
  installed: IDevsWRelsByIdWStates
  all: IDevsWRelsByIdWStates
} => {
  const isInstalled = (entry: DeviceWGroupsNStates): boolean =>
    isDeviceInstalled(entry.device.settings)

  return {
    notInstalled: fp.omitBy(isInstalled)(devsWRelsByIdWStates),
    installed: fp.pickBy(isInstalled)(devsWRelsByIdWStates),
    all: devsWRelsByIdWStates,
  }
}

/** The label over a list of devices. On a device page it names the asset the others share. */
export const getDevicesLabel = (
  count: number,
  siblingsOf: "span" | "tower" | undefined,
  t: TFunction,
): string => {
  if (siblingsOf) {
    return t(
      siblingsOf === "span"
        ? "asset_context.ALSO_ON_SPAN"
        : "asset_context.ALSO_ON_TOWER",
    )
  }
  return count > 1
    ? t("asset_context.DEVICES_COUNT", { count })
    : t("asset_context.DEVICE")
}
